import type { Locale } from "./i18n";

export const experienceModeTranslationKeys = [
  "onboardingQuestion",
  "onboardingDetail",
  "guidedName",
  "guidedDescription",
  "recommended",
  "focusedName",
  "focusedDescription",
  "changeAnytime",
  "experienceTitle",
  "interfaceStyleDetail",
  "setupGuideTitle",
  "setupGuideFutures",
  "setupGuideStickyRun",
  "setupGuideResult",
  "executionSettings",
  "resultGuideTitle",
  "resultGuideSample",
  "resultGuideRisk",
  "resultGuideValidation",
] as const;

export type ExperienceModeTranslationKey =
  (typeof experienceModeTranslationKeys)[number];

type ExperienceModeStrings = Record<ExperienceModeTranslationKey, string>;

/**
 * Complete, reviewed copy for every locale configured by the website. Keeping
 * it separate from general UI copy makes missing Guided/Focused content a
 * compile- and test-time failure rather than a silent English fallback.
 */
export const experienceModeTranslations: Record<Locale, ExperienceModeStrings> = {
  en: {
    onboardingQuestion: "How would you like to start?",
    onboardingDetail: "The default flow starts with Futures: Setup, a Run button that stays visible, then Results in the same workspace. Choose how much guidance you want.",
    guidedName: "Guided",
    guidedDescription: "Learn as you build, with examples and help interpreting results.",
    recommended: "Recommended",
    focusedName: "Focused",
    focusedDescription: "See more data with fewer explanations for faster repeat testing.",
    changeAnytime: "You can change this anytime in Settings.",
    experienceTitle: "Experience",
    interfaceStyleDetail: "This changes guidance and information density, never your strategy or results.",
    setupGuideTitle: "Build your setup",
    setupGuideFutures: "Start with Futures, then choose the market, timeframe, and strategy.",
    setupGuideStickyRun: "Set margin and risk; Run backtest stays visible while you scroll.",
    setupGuideResult: "When the run finishes, Results opens in this flow and moves to its heading.",
    executionSettings: "Execution settings",
    resultGuideTitle: "How to read this result",
    resultGuideSample: "Start with sample size and closed trades.",
    resultGuideRisk: "Check drawdown and costs before win rate.",
    resultGuideValidation: "Validate promising rules on unseen data.",
  },
  vi: {
    onboardingQuestion: "Bạn muốn bắt đầu theo cách nào?",
    onboardingDetail: "Flow mặc định bắt đầu với Futures: Thiết lập, nút Chạy backtest luôn hiện, rồi Kết quả trong cùng workspace. Chọn mức hướng dẫn bạn muốn.",
    guidedName: "Có hướng dẫn",
    guidedDescription: "Vừa thiết lập vừa học với ví dụ và hướng dẫn đọc kết quả.",
    recommended: "Đề xuất",
    focusedName: "Tập trung",
    focusedDescription: "Xem nhiều dữ liệu hơn với ít giải thích để kiểm thử lặp lại nhanh hơn.",
    changeAnytime: "Bạn có thể thay đổi bất cứ lúc nào trong Cài đặt.",
    experienceTitle: "Trải nghiệm",
    interfaceStyleDetail: "Tùy chọn này chỉ thay đổi hướng dẫn và mật độ thông tin, không bao giờ thay đổi chiến lược hay kết quả.",
    setupGuideTitle: "Xây dựng thiết lập",
    setupGuideFutures: "Bắt đầu với Futures, rồi chọn thị trường, khung thời gian và chiến lược.",
    setupGuideStickyRun: "Đặt ký quỹ và rủi ro; nút Chạy backtest luôn hiện khi bạn cuộn.",
    setupGuideResult: "Khi chạy xong, Kết quả mở ngay trong flow này và tự đưa bạn lên tiêu đề.",
    executionSettings: "Cài đặt khớp lệnh",
    resultGuideTitle: "Cách đọc kết quả",
    resultGuideSample: "Bắt đầu từ cỡ mẫu và số giao dịch đã đóng.",
    resultGuideRisk: "Kiểm tra drawdown và chi phí trước tỷ lệ thắng.",
    resultGuideValidation: "Kiểm định các quy tắc triển vọng trên dữ liệu chưa từng dùng.",
  },
  hi: {
    onboardingQuestion: "आप कैसे शुरू करना चाहेंगे?",
    onboardingDetail: "डिफ़ॉल्ट प्रवाह फ्यूचर्स से शुरू होता है: सेटअप, हमेशा दिखने वाला रन बटन और उसी वर्कस्पेस में परिणाम। अपनी पसंद का मार्गदर्शन चुनें।",
    guidedName: "मार्गदर्शित",
    guidedDescription: "उदाहरणों और परिणाम समझने में सहायता के साथ बनाते हुए सीखें।",
    recommended: "अनुशंसित",
    focusedName: "केंद्रित",
    focusedDescription: "बार-बार तेज़ी से परीक्षण करने के लिए कम व्याख्या के साथ अधिक डेटा देखें।",
    changeAnytime: "आप इसे सेटिंग्स में कभी भी बदल सकते हैं।",
    experienceTitle: "अनुभव",
    interfaceStyleDetail: "यह केवल मार्गदर्शन और जानकारी का घनत्व बदलता है, आपकी रणनीति या परिणाम नहीं।",
    setupGuideTitle: "अपना सेटअप तैयार करें",
    setupGuideFutures: "फ्यूचर्स से शुरू करें, फिर मार्केट, टाइमफ्रेम और रणनीति चुनें।",
    setupGuideStickyRun: "मार्जिन और जोखिम तय करें; स्क्रॉल करते समय रन बैकटेस्ट बटन दिखता रहता है।",
    setupGuideResult: "रन पूरा होने पर परिणाम इसी प्रवाह में खुलता है और शीर्षक पर ले जाता है।",
    executionSettings: "निष्पादन सेटिंग्स",
    resultGuideTitle: "इस परिणाम को कैसे पढ़ें",
    resultGuideSample: "पहले सैंपल का आकार और बंद ट्रेड देखें।",
    resultGuideRisk: "जीत दर से पहले ड्रॉडाउन और लागत देखें।",
    resultGuideValidation: "अच्छे लगने वाले नियमों को अनदेखे डेटा पर सत्यापित करें।",
  },
  ur: {
    onboardingQuestion: "آپ کس طرح شروع کرنا چاہیں گے؟",
    onboardingDetail: "طے شدہ فلو فیوچرز سے شروع ہوتا ہے: سیٹ اپ، ہمیشہ نظر آنے والا رن بٹن، پھر اسی ورک اسپیس میں نتائج۔ اپنی مطلوبہ رہنمائی منتخب کریں۔",
    guidedName: "رہنمائی کے ساتھ",
    guidedDescription: "مثالوں اور نتائج سمجھنے میں مدد کے ساتھ بناتے ہوئے سیکھیں۔",
    recommended: "تجویز کردہ",
    focusedName: "مرکوز",
    focusedDescription: "تیز بار بار جانچ کے لیے کم وضاحت کے ساتھ زیادہ ڈیٹا دیکھیں۔",
    changeAnytime: "آپ اسے سیٹنگز میں کسی بھی وقت تبدیل کر سکتے ہیں۔",
    experienceTitle: "تجربہ",
    interfaceStyleDetail: "یہ صرف رہنمائی اور معلومات کی کثافت بدلتا ہے، آپ کی حکمتِ عملی یا نتائج نہیں۔",
    setupGuideTitle: "اپنا سیٹ اپ بنائیں",
    setupGuideFutures: "فیوچرز سے شروع کریں، پھر مارکیٹ، ٹائم فریم اور حکمتِ عملی منتخب کریں۔",
    setupGuideStickyRun: "مارجن اور خطرہ مقرر کریں؛ اسکرول کے دوران رن بیک ٹیسٹ بٹن نظر آتا رہتا ہے۔",
    setupGuideResult: "رن مکمل ہونے پر نتائج اسی فلو میں کھلتے ہیں اور عنوان تک لے جاتے ہیں۔",
    executionSettings: "عمل درآمد کی ترتیبات",
    resultGuideTitle: "اس نتیجے کو کیسے پڑھیں",
    resultGuideSample: "پہلے نمونے کا حجم اور بند ٹریڈز دیکھیں۔",
    resultGuideRisk: "جیت کی شرح سے پہلے ڈرا ڈاؤن اور اخراجات دیکھیں۔",
    resultGuideValidation: "امید افزا قواعد کو نئے ڈیٹا پر جانچیں۔",
  },
  th: {
    onboardingQuestion: "คุณต้องการเริ่มต้นแบบใด?",
    onboardingDetail: "ขั้นตอนเริ่มต้นใช้ฟิวเจอร์ส: ตั้งค่า ปุ่มรันที่มองเห็นตลอด แล้วดูผลลัพธ์ในพื้นที่เดียวกัน เลือกระดับคำแนะนำที่ต้องการ",
    guidedName: "มีคำแนะนำ",
    guidedDescription: "เรียนรู้ไปพร้อมกับการตั้งค่า ด้วยตัวอย่างและคำแนะนำในการอ่านผลลัพธ์",
    recommended: "แนะนำ",
    focusedName: "เน้นงาน",
    focusedDescription: "ดูข้อมูลมากขึ้นพร้อมคำอธิบายที่น้อยลง เพื่อทดสอบซ้ำได้เร็วขึ้น",
    changeAnytime: "คุณเปลี่ยนตัวเลือกนี้ได้ทุกเมื่อในการตั้งค่า",
    experienceTitle: "รูปแบบการใช้งาน",
    interfaceStyleDetail: "ตัวเลือกนี้เปลี่ยนเฉพาะคำแนะนำและความหนาแน่นของข้อมูล ไม่เปลี่ยนกลยุทธ์หรือผลลัพธ์ของคุณ",
    setupGuideTitle: "สร้างการตั้งค่าของคุณ",
    setupGuideFutures: "เริ่มด้วยฟิวเจอร์ส แล้วเลือกตลาด กรอบเวลา และกลยุทธ์",
    setupGuideStickyRun: "กำหนดมาร์จินและความเสี่ยง ปุ่มรันแบ็กเทสต์จะยังมองเห็นขณะเลื่อน",
    setupGuideResult: "เมื่อรันเสร็จ ผลลัพธ์จะเปิดในขั้นตอนเดิมและเลื่อนไปที่หัวข้อ",
    executionSettings: "การตั้งค่าการส่งคำสั่ง",
    resultGuideTitle: "วิธีอ่านผลลัพธ์นี้",
    resultGuideSample: "เริ่มจากขนาดตัวอย่างและจำนวนรายการที่ปิดแล้ว",
    resultGuideRisk: "ตรวจสอบ Drawdown และต้นทุนก่อนดูอัตราชนะ",
    resultGuideValidation: "ตรวจสอบกฎที่มีแนวโน้มดีด้วยข้อมูลที่ยังไม่เคยใช้",
  },
  bn: {
    onboardingQuestion: "আপনি কীভাবে শুরু করতে চান?",
    onboardingDetail: "ডিফল্ট প্রবাহ ফিউচারস দিয়ে শুরু হয়: সেটআপ, সব সময় দেখা যায় এমন রান বোতাম, তারপর একই ওয়ার্কস্পেসে ফলাফল। নির্দেশনার মাত্রা বেছে নিন।",
    guidedName: "নির্দেশিত",
    guidedDescription: "উদাহরণ ও ফলাফল বোঝার সহায়তা নিয়ে তৈরি করতে করতে শিখুন।",
    recommended: "প্রস্তাবিত",
    focusedName: "কেন্দ্রিত",
    focusedDescription: "দ্রুত পুনরাবৃত্ত পরীক্ষার জন্য কম ব্যাখ্যাসহ বেশি ডেটা দেখুন।",
    changeAnytime: "আপনি সেটিংসে যেকোনো সময় এটি পরিবর্তন করতে পারবেন।",
    experienceTitle: "অভিজ্ঞতা",
    interfaceStyleDetail: "এটি শুধু নির্দেশনা ও তথ্যের ঘনত্ব বদলায়, আপনার কৌশল বা ফলাফল নয়।",
    setupGuideTitle: "আপনার সেটআপ তৈরি করুন",
    setupGuideFutures: "ফিউচারস দিয়ে শুরু করুন, তারপর মার্কেট, টাইমফ্রেম ও কৌশল বেছে নিন।",
    setupGuideStickyRun: "মার্জিন ও ঝুঁকি ঠিক করুন; স্ক্রল করলেও রান ব্যাকটেস্ট বোতাম দেখা যাবে।",
    setupGuideResult: "রান শেষ হলে ফলাফল এই প্রবাহেই খুলবে এবং শিরোনামে নিয়ে যাবে।",
    executionSettings: "কার্যকরকরণ সেটিংস",
    resultGuideTitle: "এই ফলাফল কীভাবে পড়বেন",
    resultGuideSample: "প্রথমে নমুনার আকার ও বন্ধ হওয়া ট্রেড দেখুন।",
    resultGuideRisk: "জয়ের হারের আগে ড্রডাউন ও খরচ দেখুন।",
    resultGuideValidation: "সম্ভাবনাময় নিয়মগুলো নতুন ডেটায় যাচাই করুন।",
  },
  id: {
    onboardingQuestion: "Bagaimana Anda ingin memulai?",
    onboardingDetail: "Alur default dimulai dengan Futures: Pengaturan, tombol Run yang tetap terlihat, lalu Hasil di workspace yang sama. Pilih tingkat panduan Anda.",
    guidedName: "Terpandu",
    guidedDescription: "Belajar sambil menyusun, dengan contoh dan bantuan untuk memahami hasil.",
    recommended: "Disarankan",
    focusedName: "Fokus",
    focusedDescription: "Lihat lebih banyak data dengan lebih sedikit penjelasan agar pengujian ulang lebih cepat.",
    changeAnytime: "Anda dapat mengubahnya kapan saja di Pengaturan.",
    experienceTitle: "Pengalaman",
    interfaceStyleDetail: "Ini hanya mengubah panduan dan kepadatan informasi, bukan strategi atau hasil Anda.",
    setupGuideTitle: "Susun pengaturan Anda",
    setupGuideFutures: "Mulai dengan Futures, lalu pilih pasar, timeframe, dan strategi.",
    setupGuideStickyRun: "Atur margin dan risiko; tombol Jalankan backtest tetap terlihat saat Anda menggulir.",
    setupGuideResult: "Saat run selesai, Hasil terbuka di alur ini dan berpindah ke judulnya.",
    executionSettings: "Pengaturan eksekusi",
    resultGuideTitle: "Cara membaca hasil ini",
    resultGuideSample: "Mulai dari ukuran sampel dan trade yang sudah ditutup.",
    resultGuideRisk: "Periksa drawdown dan biaya sebelum win rate.",
    resultGuideValidation: "Validasi aturan yang menjanjikan dengan data baru.",
  },
  fil: {
    onboardingQuestion: "Paano mo gustong magsimula?",
    onboardingDetail: "Nagsisimula sa Futures ang default flow: Setup, Run button na laging nakikita, saka Results sa parehong workspace. Piliin ang dami ng gabay.",
    guidedName: "May gabay",
    guidedDescription: "Matuto habang bumubuo gamit ang mga halimbawa at tulong sa pag-unawa ng resulta.",
    recommended: "Inirerekomenda",
    focusedName: "Nakatuon",
    focusedDescription: "Makakita ng mas maraming data at mas kaunting paliwanag para sa mas mabilis na paulit-ulit na pagsubok.",
    changeAnytime: "Maaari mo itong baguhin anumang oras sa Mga Setting.",
    experienceTitle: "Karanasan",
    interfaceStyleDetail: "Gabay at dami lang ng impormasyong ipinapakita ang nagbabago, hindi ang iyong estratehiya o mga resulta.",
    setupGuideTitle: "Buuin ang iyong setup",
    setupGuideFutures: "Magsimula sa Futures, saka piliin ang market, timeframe, at strategy.",
    setupGuideStickyRun: "Itakda ang margin at risk; nananatiling nakikita ang Run backtest habang nag-i-scroll.",
    setupGuideResult: "Kapag tapos ang run, bubukas ang Results sa flow na ito at lilipat sa heading nito.",
    executionSettings: "Mga setting ng execution",
    resultGuideTitle: "Paano basahin ang resultang ito",
    resultGuideSample: "Unahin ang laki ng sample at mga naisara nang trade.",
    resultGuideRisk: "Suriin ang drawdown at gastos bago ang win rate.",
    resultGuideValidation: "Subukan ang mahuhusay na panuntunan sa bagong data.",
  },
  "es-419": {
    onboardingQuestion: "¿Cómo te gustaría empezar?",
    onboardingDetail: "El flujo predeterminado empieza con Futuros: Configuración, un botón Ejecutar siempre visible y Resultados en el mismo espacio. Elige cuánta orientación quieres.",
    guidedName: "Guiado",
    guidedDescription: "Aprende mientras configuras, con ejemplos y ayuda para interpretar los resultados.",
    recommended: "Recomendado",
    focusedName: "Enfocado",
    focusedDescription: "Consulta más datos con menos explicaciones para repetir pruebas más rápido.",
    changeAnytime: "Puedes cambiarlo en cualquier momento desde Configuración.",
    experienceTitle: "Experiencia",
    interfaceStyleDetail: "Esto solo cambia la orientación y la densidad de información, nunca tu estrategia ni tus resultados.",
    setupGuideTitle: "Crea tu configuración",
    setupGuideFutures: "Empieza con Futuros y elige mercado, temporalidad y estrategia.",
    setupGuideStickyRun: "Define margen y riesgo; Ejecutar backtest permanece visible al desplazarte.",
    setupGuideResult: "Al terminar, Resultados se abre en este flujo y se desplaza a su encabezado.",
    executionSettings: "Ajustes de ejecución",
    resultGuideTitle: "Cómo leer este resultado",
    resultGuideSample: "Empieza por el tamaño de la muestra y las operaciones cerradas.",
    resultGuideRisk: "Revisa el drawdown y los costos antes que la tasa de acierto.",
    resultGuideValidation: "Valida las reglas prometedoras con datos no utilizados.",
  },
  "pt-BR": {
    onboardingQuestion: "Como você gostaria de começar?",
    onboardingDetail: "O fluxo padrão começa em Futuros: Configuração, botão Executar sempre visível e Resultados no mesmo espaço. Escolha quanto de orientação deseja.",
    guidedName: "Guiado",
    guidedDescription: "Aprenda enquanto configura, com exemplos e ajuda para interpretar os resultados.",
    recommended: "Recomendado",
    focusedName: "Focado",
    focusedDescription: "Veja mais dados com menos explicações para repetir testes com mais rapidez.",
    changeAnytime: "Você pode alterar isso a qualquer momento em Ajustes.",
    experienceTitle: "Experiência",
    interfaceStyleDetail: "Isso altera apenas a orientação e a densidade das informações, nunca sua estratégia ou seus resultados.",
    setupGuideTitle: "Monte sua configuração",
    setupGuideFutures: "Comece com Futuros e escolha mercado, timeframe e estratégia.",
    setupGuideStickyRun: "Defina margem e risco; Executar backtest continua visível durante a rolagem.",
    setupGuideResult: "Ao terminar, Resultados abre neste fluxo e avança para o cabeçalho.",
    executionSettings: "Ajustes de execução",
    resultGuideTitle: "Como interpretar este resultado",
    resultGuideSample: "Comece pelo tamanho da amostra e pelos trades encerrados.",
    resultGuideRisk: "Verifique o drawdown e os custos antes da taxa de acerto.",
    resultGuideValidation: "Valide regras promissoras com dados ainda não utilizados.",
  },
  ja: {
    onboardingQuestion: "どのように始めますか？",
    onboardingDetail: "標準フローは先物から始まり、設定、常に表示される実行ボタン、同じワークスペース内の結果へ進みます。必要なガイド量を選んでください。",
    guidedName: "ガイド付き",
    guidedDescription: "例と結果の読み方を確認しながら、設定を通して学べます。",
    recommended: "おすすめ",
    focusedName: "フォーカス",
    focusedDescription: "説明を減らしてより多くのデータを表示し、再テストをすばやく行えます。",
    changeAnytime: "この設定は「設定」からいつでも変更できます。",
    experienceTitle: "操作スタイル",
    interfaceStyleDetail: "変わるのはガイドと情報量だけで、戦略や結果には影響しません。",
    setupGuideTitle: "設定を組み立てる",
    setupGuideFutures: "先物から始め、マーケット、時間足、戦略を選びます。",
    setupGuideStickyRun: "証拠金とリスクを設定します。スクロール中もバックテスト実行ボタンは表示されます。",
    setupGuideResult: "実行が完了すると、このフロー内で結果が開き、見出しまで移動します。",
    executionSettings: "約定設定",
    resultGuideTitle: "結果の読み方",
    resultGuideSample: "まずサンプル数と決済済み取引数を確認します。",
    resultGuideRisk: "勝率より先にドローダウンとコストを確認します。",
    resultGuideValidation: "有望なルールは未使用データで検証します。",
  },
  ko: {
    onboardingQuestion: "어떻게 시작하시겠어요?",
    onboardingDetail: "기본 흐름은 선물, 설정, 스크롤 중에도 보이는 실행 버튼, 같은 워크스페이스의 결과 순서입니다. 원하는 안내 수준을 선택하세요.",
    guidedName: "안내형",
    guidedDescription: "예시와 결과 해석 도움을 보며 설정 과정에서 배워 보세요.",
    recommended: "추천",
    focusedName: "집중형",
    focusedDescription: "설명을 줄이고 더 많은 데이터를 확인해 반복 테스트를 빠르게 진행하세요.",
    changeAnytime: "설정에서 언제든 변경할 수 있습니다.",
    experienceTitle: "사용 환경",
    interfaceStyleDetail: "안내와 정보 밀도만 변경되며 전략이나 결과에는 영향을 주지 않습니다.",
    setupGuideTitle: "설정 구성하기",
    setupGuideFutures: "선물로 시작한 뒤 시장, 시간대, 전략을 선택하세요.",
    setupGuideStickyRun: "증거금과 리스크를 설정하세요. 스크롤 중에도 백테스트 실행 버튼이 보입니다.",
    setupGuideResult: "실행이 끝나면 이 흐름에서 결과가 열리고 제목으로 이동합니다.",
    executionSettings: "체결 설정",
    resultGuideTitle: "결과 읽는 방법",
    resultGuideSample: "먼저 표본 크기와 청산된 거래 수를 확인하세요.",
    resultGuideRisk: "승률보다 드로다운과 비용을 먼저 확인하세요.",
    resultGuideValidation: "유망한 규칙은 사용하지 않은 데이터로 검증하세요.",
  },
  ru: {
    onboardingQuestion: "Как вы хотите начать?",
    onboardingDetail: "Стандартный сценарий начинается с фьючерсов: настройка, постоянно видимая кнопка запуска и результаты в том же пространстве. Выберите уровень подсказок.",
    guidedName: "С подсказками",
    guidedDescription: "Учитесь в процессе настройки с примерами и помощью в интерпретации результатов.",
    recommended: "Рекомендуется",
    focusedName: "Компактный",
    focusedDescription: "Просматривайте больше данных и меньше пояснений, чтобы быстрее повторять тесты.",
    changeAnytime: "Это можно изменить в настройках в любое время.",
    experienceTitle: "Режим работы",
    interfaceStyleDetail: "Меняются только подсказки и плотность информации, но не ваша стратегия или результаты.",
    setupGuideTitle: "Соберите настройки",
    setupGuideFutures: "Начните с фьючерсов, затем выберите рынок, таймфрейм и стратегию.",
    setupGuideStickyRun: "Задайте маржу и риск; кнопка запуска бэктеста остаётся видимой при прокрутке.",
    setupGuideResult: "После завершения результаты откроются в этом сценарии с переходом к заголовку.",
    executionSettings: "Настройки исполнения",
    resultGuideTitle: "Как читать результат",
    resultGuideSample: "Сначала оцените размер выборки и число закрытых сделок.",
    resultGuideRisk: "Проверьте просадку и издержки до процента прибыльных сделок.",
    resultGuideValidation: "Проверяйте перспективные правила на неиспользованных данных.",
  },
  tr: {
    onboardingQuestion: "Nasıl başlamak istersiniz?",
    onboardingDetail: "Varsayılan akış Vadeli ile başlar: Kurulum, kaydırırken görünen Çalıştır düğmesi ve aynı alandaki Sonuçlar. Rehberlik düzeyini seçin.",
    guidedName: "Rehberli",
    guidedDescription: "Örnekler ve sonuçları yorumlama yardımıyla oluştururken öğrenin.",
    recommended: "Önerilen",
    focusedName: "Odaklı",
    focusedDescription: "Tekrarlanan testleri hızlandırmak için daha az açıklamayla daha fazla veri görün.",
    changeAnytime: "Bunu Ayarlar'dan istediğiniz zaman değiştirebilirsiniz.",
    experienceTitle: "Deneyim",
    interfaceStyleDetail: "Bu yalnızca rehberliği ve bilgi yoğunluğunu değiştirir; stratejinizi veya sonuçlarınızı değiştirmez.",
    setupGuideTitle: "Kurulumunuzu oluşturun",
    setupGuideFutures: "Vadeli ile başlayın; ardından piyasa, zaman dilimi ve stratejiyi seçin.",
    setupGuideStickyRun: "Marjı ve riski ayarlayın; Backtest çalıştır düğmesi kaydırırken görünür kalır.",
    setupGuideResult: "Çalışma bitince Sonuçlar bu akışta açılır ve başlığa gider.",
    executionSettings: "Gerçekleşme ayarları",
    resultGuideTitle: "Bu sonuç nasıl okunur",
    resultGuideSample: "Önce örneklem büyüklüğüne ve kapanmış işlemlere bakın.",
    resultGuideRisk: "Kazanma oranından önce düşüşü ve maliyetleri kontrol edin.",
    resultGuideValidation: "Umut veren kuralları kullanılmamış verilerde doğrulayın.",
  },
  uk: {
    onboardingQuestion: "Як ви хочете почати?",
    onboardingDetail: "Стандартний сценарій починається з ф’ючерсів: налаштування, постійно видима кнопка запуску та результати в тому самому просторі. Виберіть рівень підказок.",
    guidedName: "З підказками",
    guidedDescription: "Навчайтеся під час налаштування за допомогою прикладів і пояснень результатів.",
    recommended: "Рекомендовано",
    focusedName: "Зосереджений",
    focusedDescription: "Переглядайте більше даних із меншою кількістю пояснень, щоб швидше повторювати тести.",
    changeAnytime: "Це можна будь-коли змінити в налаштуваннях.",
    experienceTitle: "Режим роботи",
    interfaceStyleDetail: "Змінюються лише підказки та щільність інформації, але не ваша стратегія чи результати.",
    setupGuideTitle: "Створіть налаштування",
    setupGuideFutures: "Почніть із ф’ючерсів, потім виберіть ринок, таймфрейм і стратегію.",
    setupGuideStickyRun: "Задайте маржу й ризик; кнопка запуску бектесту залишається видимою під час прокручування.",
    setupGuideResult: "Після завершення результати відкриються в цьому сценарії з переходом до заголовка.",
    executionSettings: "Налаштування виконання",
    resultGuideTitle: "Як читати результат",
    resultGuideSample: "Спочатку оцініть розмір вибірки й кількість закритих угод.",
    resultGuideRisk: "Перевірте просадку й витрати до відсотка прибуткових угод.",
    resultGuideValidation: "Перевіряйте перспективні правила на невикористаних даних.",
  },
};

export function createExperienceModeTranslator(locale: Locale) {
  return (key: ExperienceModeTranslationKey): string =>
    experienceModeTranslations[locale][key];
}
